<template>
    <div class="w-full">
        <div class="inline">
            <page-headline>{{$t('pages.insights.auctions.insights_auctions')}}</page-headline>
        </div>
        <div class="mb-4 flex items-center p-3 w-full bg-gray-200 overflow-x-auto">
            <div class="flex items-center">
                <search-filter v-model="form.search" class="w-full max-w-md mr-4" @reset="reset">
                    <label class="mt-2 block py-1 pl-3 bg-ezdBlue text-white">{{$t('pages.insights.auctions.type')}}</label>
                    <select v-model="form.type" class="w-full form-select">
                        <option value="-1">{{$t('all.auction_types.all')}}</option>
                        <option value="3-L">{{$t('all.auction_types.livestyle')}}</option>
                        <option value="3-P">{{$t('all.auction_types.secretsale')}}</option>
                    </select>
                    <label class="mt-2 block py-1 pl-3 bg-ezdBlue text-white">{{$t('all.start_date_from_to')}}</label>
                    <div class="border-2 border-t-0 py-2">
                        <input class="text-gray-700 text-md" type="date" name="from" value="2020-08-25" v-model="form.dateStart"/> - <input class="text-gray-700 text-md" type="date" name="to" v-model="form.dateEnd"/>
                    </div>
                </search-filter>
            </div>
            <div class="inline-block flex text-nowrap text-sm justify-center mr-3 pl-3 border-gray-500 border-l-2">
                {{$t('pages.insights.auctions.grouped_by')}}
            </div>
            <div class="relative">
                <select v-model="form.group" class="inline-block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 leading-tight focus:outline-none focus:shadow-outline">
                    <option value="day">{{$t('pages.insights.auctions.grouped_by_values.day')}}</option>
                    <option value="day-hour">{{$t('pages.insights.auctions.grouped_by_values.day_hour')}}</option>
                    <option value="day-show">{{$t('pages.insights.auctions.grouped_by_values.day_start')}}</option>
                    <option value="week">{{$t('pages.insights.auctions.grouped_by_values.week')}}</option>
                    <option value="month">{{$t('pages.insights.auctions.grouped_by_values.month')}}</option>
                    <option value="campaign">{{$t('pages.insights.auctions.grouped_by_values.campaign')}}</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
            </div>
            <div class="ml-5 flex text-sm" v-if="form.type == '-1'">
                <div>
                    <input v-model="form.groupByAuctionType" id="chb_groupByAuctionType" type="checkbox" class="mr-1" value="1"/>
                </div>
                <div>
                    <label for="chb_groupByAuctionType" class="pl-2">{{$t('pages.insights.auctions.auction_type')}}</label>
                </div>
            </div>
            <div class="ml-5 flex text-sm">
                <div>
                    <input v-model="form.groupBySalesChannel" id="chb_groupBySalesChannel" type="checkbox" class="mr-1" value="1"/>
                </div>
                <div>
                    <label for="chb_groupBySalesChannel" class="pl-2">{{$t('pages.insights.auctions.distribution_channel')}}</label>
                </div>
            </div>
        </div>
        <div class="flex w-full justify-end pb-4">
            <div class="bg-ezdBlue text-white px-2 py-1 cursor-pointer">
                <a :href="url" target="_blank">{{$t('pages.insights.auctions.export_csv')}}</a>
            </div>
        </div>
        <div class="w-full">
            <div class="text-center text-sm" v-if="!this.hasData">{{$t('all.no_data_for_selected_period')}}</div>
            <div class="bg-white shadow overflow-x-auto mb-6" v-if="hasData">
                <table class="w-full whitespace-nowrap table-auto">
                    <thead>
                        <tr class="text-left border-b-2 table-headline">
                            <th class="px-6 py-3 font-normal">{{$t('pages.insights.auctions.auction_columns.time_period')}}</th>
                            <th v-if="hasAuctionTypeGrouping" class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.auction_type')}}</th>
                            <th v-if="hasSalesChannelGrouping" class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.distribution_channel')}}</th>
                            <th class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.number_auctions')}}</th>
                            <th class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.number_bids')}}</th>
                            <th class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.number_customers')}}</th>
                            <th class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.number_orders')}}</th>
                            <th class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.sum_sales')}}</th>
                            <th class="px-6 text-center font-normal">{{$t('pages.insights.auctions.auction_columns.average_bids_per_customer')}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="stat in stats" class="border-b hover:bg-gray-300 even:bg-gray-200">
                            <td class="px-6 py-3" v-if="isDate(stat.period)">{{ stat.period | formatDate }}</td>
                            <td class="px-6 py-3" v-else>{{ stat.period }}</td>
                            <td v-if="hasAuctionTypeGrouping" class="px-6 py-3 text-center">{{ mapAuctionTypeName(stat.auction_type_name, stat.type) }}</td>
                            <td v-if="hasSalesChannelGrouping" class="px-6 py-3 text-center">{{ stat.sales_channel }}</td>
                            <td class="px-6 py-3 text-center">{{ stat.auctions_quantity }}</td>
                            <td class="px-6 py-3 text-center">{{ stat.bids_quantity }}</td>
                            <td class="px-6 py-3 text-center">{{ stat.unique_customers }}</td>
                            <td class="px-6 py-3 text-center">{{ stat.orders_quantity }}</td>
                            <td class="px-6 py-3 text-center">{{ parseFloat(stat.gross_sales) | toCurrency}}</td>
                            <td class="px-6 py-3 text-center">{{ isNaN((stat.bids_quantity / stat.unique_customers)) ? 0 : (stat.bids_quantity / stat.unique_customers).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
    import Layout from '@/Shared/Layout';
    import Pagination from '@/Shared/Pagination';
    import Icon from '@/Shared/Icon';
    import PageHeadline from "@/Shared/PageHeadline";
    import Status from '@/Shared/Status';
    import pickBy from 'lodash/pickBy';
    import throttle from 'lodash/throttle';
    import AuctionState from "@/mixins/AuctionState";
    import SearchFilter from "@/Shared/SearchFilter";
    export default {
        metaInfo() {
            return {
                title: this.$t('pages.insights.auctions.insights_auctions')
            }
        },
        mixins: [AuctionState],
        layout: Layout,
        remember: 'form',
        components: {
            Pagination,
            Icon,
            PageHeadline,
            Status,
            SearchFilter
        },
        props: {
            stats: Array,
            group: Object,
            states: Object
        },
        data() {
            return {
                form: {
                    group: this.group.time,
                    groupByAuctionType: this.group.auctionType,
                    groupBySalesChannel: this.group.salesChannel,
                    type: this.states.type,
                    dateStart: this.states.dateStart,
                    dateEnd: this.states.dateEnd
                }
            }
        },
        methods: {
            isDate(value) {
                var date = Date.parse(value);
                return !isNaN(date);
            },
            mapAuctionTypeName(auctionTypeId, auctionType) {
                switch (auctionTypeId) {
                    case 1:
                        return ('P' === auctionType) ? this.$t('all.auction_names.parallel_auction') : this.$t('all.auction_names.daily_auction') ;
                    case 2:
                        return ('P' === auctionType) ? this.$t('all.auction_names.parallel_auction_bid_up') : ('T' === auctionType ? this.$t('all.auction_names.daily_auction_bid_up') : this.$t('all.auction_names.oba'));
                    case 3:
                        return ('L' === auctionType) ? this.$t('all.auction_names.livestyle') : ('P' === auctionType ? this.$t('all.auction_names.secretsale') : this.$t('all.auction_names.custom'));
                }
            },
            reset() {
                this.form = { type: -1, group: 'day' }
            }
        },
        computed: {
            hasData() {
              return this.stats.length > 0;
            },
            hasAuctionTypeGrouping() {
                return ('auction_type_name' in this.stats[0]);
            },
            hasSalesChannelGrouping() {
                return ('sales_channel' in this.stats[0]);
            },
            url() {
                let query = pickBy(this.form);
                query.format = 'CSV';
                return this.route('insights.auctions', Object.keys(query).length ? query : {});
            }
        },
        watch: {
            form: {
                handler: throttle(function() {
                    let query = pickBy(this.form);
                    this.$inertia.get(this.route('insights.auctions', Object.keys(query).length ? query : {}), {}, { preserveState: true })
                }, 150),
                deep: true
            }
        },
    }
</script>
